import React, { useMemo } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import {
  User,
  Bot,
  AlertCircle,
  Info,
  Loader2
} from 'lucide-react';
import MessageFormatter from './MessageFormatter';

const MessageStream = ({
  messages = [],
  streamingMessage = '',
  isStreaming = false,
  isLoading = false,
  error = null,
}) => {
  // Combine regular messages with streaming message
  const displayMessages = useMemo(() => {
    const allMessages = [...messages];

    if (isStreaming && streamingMessage) {
      allMessages.push({
        id: 'streaming',
        type: 'assistant',
        content: streamingMessage,
        timestamp: new Date(),
        metadata: { isStreaming: true },
      });
    }

    return allMessages;
  }, [messages, streamingMessage, isStreaming]);

  const getMessageAvatar = (message) => {
    const baseClass = "w-10 h-10 rounded-full flex items-center justify-center";
    switch (message.type) {
      case 'user':
        return (
          <div className={`${baseClass} bg-primary/100`}>
            <User className="h-5 w-5 text-white" />
          </div>
        );
      case 'assistant':
        return (
          <div className={`${baseClass} bg-secondary`}>
            <Bot className="h-5 w-5 text-white" />
          </div>
        );
      case 'system':
        return (
          <div className={`${baseClass} bg-muted0`}>
            <Info className="h-5 w-5 text-white" />
          </div>
        );
      default:
        return (
          <div className={`${baseClass} bg-muted`}>
            <Info className="h-5 w-5 text-muted-foreground" />
          </div>
        );
    }
  };

  const getMessageAlignment = (message) => {
    return message.type === 'user' ? 'justify-end' : 'justify-start';
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const renderMessage = (message) => {
    const isUser = message.type === 'user';
    const isSystem = message.type === 'system';
    const isStreamingMsg = message.metadata?.isStreaming;

    return (
      <div
        key={message.id}
        className={`flex items-start ${getMessageAlignment(message)} mb-6 px-4`}
      >
        {!isUser && (
          <div className="mr-3 mt-1 flex-shrink-0">
            {getMessageAvatar(message)}
          </div>
        )}

        <div className="max-w-[70%] min-w-[200px]">
          {/* Message Content */}
          <Card
            className={`p-4 ${
              isUser
                ? 'bg-primary/100 text-white rounded-br-sm'
                : isSystem
                ? 'bg-muted rounded-bl-sm'
                : 'bg-card rounded-bl-sm'
            }`}
          >
            {/* System Message Special Styling */}
            {isSystem && (
              <Badge variant="secondary" className="mb-2 text-xs">
                系统
              </Badge>
            )}

            {/* Message Text */}
            <div>
              {isUser ? (
                <p className="text-base break-words">
                  {message.content}
                </p>
              ) : (
                <MessageFormatter content={message.content} />
              )}
            </div>

            {/* Streaming Indicator */}
            {isStreamingMsg && (
              <div className="flex items-center mt-2 opacity-70">
                <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                <span className="text-xs">AI正在输入...</span>
              </div>
            )}

            {/* File Attachments */}
            {message.files && message.files.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {message.files.map((file, index) => (
                  <Badge key={index} variant="secondary" className="text-xs">
                    {file.name}
                  </Badge>
                ))}
              </div>
            )}
          </Card>

          {/* Timestamp */}
          <span
            className={`text-xs text-muted-foreground block mt-1 ${
              isUser ? 'text-right' : 'text-left'
            }`}
          >
            {formatTimestamp(message.timestamp)}
          </span>
        </div>

        {isUser && (
          <div className="ml-3 mt-1 flex-shrink-0">
            {getMessageAvatar(message)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className="flex-1 overflow-auto overflow-x-hidden py-4 bg-muted h-full"
      style={{
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(0,0,0,0.3) rgba(0,0,0,0.1)'
      }}
    >
      {/* Error Display */}
      {error && (
        <div className="px-4 mb-4">
          <Alert variant="destructive" className="rounded-lg">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error.message || '加载消息时发生错误。'}
            </AlertDescription>
          </Alert>
        </div>
      )}

      {/* Loading Indicator */}
      {isLoading && displayMessages.length === 0 && (
        <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground">
          <Loader2 className="h-8 w-8 mb-4 animate-spin" />
          <span className="text-sm">正在加载对话...</span>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && displayMessages.length === 0 && !error && (
        <div className="flex flex-col items-center justify-center h-[300px] text-center text-muted-foreground px-8">
          <Bot className="h-16 w-16 mb-4 opacity-50" />
          <h3 className="text-lg font-medium mb-2">
            欢迎使用AI助手
          </h3>
          <p className="text-sm">
            您可以询问关于业务运营、库存管理、销售或任何其他问题。
            我来帮助您！
          </p>
        </div>
      )}

      {/* Messages */}
      {displayMessages.map(renderMessage)}

      {/* Additional loading indicator for ongoing operations */}
      {isLoading && displayMessages.length > 0 && (
        <div className="flex items-center justify-center py-4 text-muted-foreground">
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          <span className="text-sm">处理中...</span>
        </div>
      )}
    </div>
  );
};

export default MessageStream;
